import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Input } from '@/components/Controls/Input';
import { FormField } from '@/components/Form/FormField';
import { FieldValues, Path } from 'react-hook-form';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { WarningTag } from '@/components/Tag/WarningTag';
import { FormManagementProps } from '@/type/form.types';
import { TitleSection } from '@/components/Header/Section/TitleSection';

export const UserPasswordManagement = <TSchema extends FieldValues>({
  t,
  register,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  return (
    <form onSubmit={onSubmit}>
      <Card
        title={
          <TitleSection
            text={t('common:personalInformations')}
            defaultIconClassName="ki-security-user text-[26.5px]"
          />
        }
        footer={
          <Button
            buttonClassName="px-8"
            text={
              submitDisabled ? `${t('common:loading')}...` : t('common:save')
            }
            disabled={submitDisabled}
          />
        }
        bodyClassName="pt-5 pb-8 flex flex-col gap-7"
      >
        <WarningTag
          text={t('common:ensurePasswordIsCommunicatedToTheUser')}
          icon={
            <i className="ki-solid ki-information-2 text-blue-4982FC text-xl" />
          }
        />
        <div className="flex flex-col gap-5">
          <FormField
            text={t('common:newPassword')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('password' as Path<TSchema>))}
                errorMessage={errors && (errors.password?.message as string)}
                placeholder={t('common:password')}
                type={showPassword ? 'text' : 'password'}
                rightIcon={
                  <i
                    className={twMerge(
                      'ki-filled text-gray-500 text-xl hover:cursor-pointer',
                      showPassword ? 'ki-eye-slash' : 'ki-eye'
                    )}
                    onClick={() => setShowPassword((prevState) => !prevState)}
                  />
                }
                inputClassName="h-10"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:confirmNewPassword')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('passwordConfirm' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.passwordConfirm?.message as string)
                }
                placeholder={t('common:reEnterPassword')}
                type={showPasswordConfirm ? 'text' : 'password'}
                rightIcon={
                  <i
                    className={twMerge(
                      'ki-filled text-gray-500 text-xl hover:cursor-pointer',
                      showPasswordConfirm ? 'ki-eye-slash' : 'ki-eye'
                    )}
                    onClick={() =>
                      setShowPasswordConfirm((prevState) => !prevState)
                    }
                  />
                }
                inputClassName="h-10"
              />
            </div>
          </FormField>
        </div>
      </Card>
    </form>
  );
};
